import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import { usersApi } from '@/api';
import useUserStore from '@/stores/useUserStore';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';

const ROLE_OPTIONS = [
  { value: 'admin', label: 'Administrateur' },
  { value: 'expert_senior', label: 'Expert Senior' },
  { value: 'expert_junior', label: 'Expert Junior' },
  { value: 'assistante', label: 'Assistante' },
];

export default function UserFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const { createUser, updateUser } = useUserStore();

  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(isEdit);
  const [error, setError] = useState(null);

  const [form, setForm] = useState({
    email: '',
    prenom: '',
    nom: '',
    role: 'expert_junior',
    password: '',
    is_active: true,
  });

  useEffect(() => {
    if (!isEdit) return;

    const fetchUser = async () => {
      try {
        const user = await usersApi.get(id);
        setForm({
          email: user.email || '',
          prenom: user.prenom || '',
          nom: user.nom || '',
          role: user.role || 'expert_junior',
          password: '',
          is_active: user.is_active ?? true,
        });
      } catch {
        setError('Utilisateur introuvable.');
      } finally {
        setFetchLoading(false);
      }
    };
    fetchUser();
  }, [id, isEdit]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const payload = {
      email: form.email,
      prenom: form.prenom,
      nom: form.nom,
      role: form.role,
    };

    if (form.password) {
      payload.password = form.password;
    }

    if (isEdit) {
      payload.is_active = form.is_active;
    }

    try {
      if (isEdit) {
        await updateUser(id, payload);
      } else {
        payload.password = form.password;
        await createUser(payload);
      }
      navigate('/admin/utilisateurs');
    } catch (err) {
      setError(err.message || 'Une erreur est survenue.');
      setLoading(false);
    }
  };

  const isValid =
    form.email.trim() !== '' &&
    form.nom.trim() !== '' &&
    form.prenom.trim() !== '' &&
    form.role !== '' &&
    (isEdit || form.password.length >= 6);

  if (fetchLoading) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <p className="text-slate-500">Chargement...</p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/admin/utilisateurs')}
          className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg transition"
        >
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-slate-900">
            {isEdit ? "Modifier l'utilisateur" : 'Nouvel utilisateur'}
          </h1>
          <p className="text-slate-600 mt-1">
            {isEdit
              ? "Modifiez les informations de l'utilisateur"
              : 'Créez un nouveau compte utilisateur'}
          </p>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit}>
        <Card className="p-6 space-y-6">
          {/* Informations */}
          <Input
            label="Email"
            type="email"
            placeholder="jean.dupont@carex.fr"
            value={form.email}
            onChange={(e) => handleChange('email', e.target.value)}
            required
          />

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Prénom"
              placeholder="Jean"
              value={form.prenom}
              onChange={(e) => handleChange('prenom', e.target.value)}
              required
            />
            <Input
              label="Nom"
              placeholder="Dupont"
              value={form.nom}
              onChange={(e) => handleChange('nom', e.target.value)}
              required
            />
          </div>

          <Select
            label="Rôle"
            name="role"
            options={ROLE_OPTIONS}
            value={form.role}
            onChange={(e) => handleChange('role', e.target.value)}
          />

          {/* Mot de passe */}
          <div className="border-t pt-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Mot de passe</h2>
            <Input
              label={isEdit ? 'Nouveau mot de passe' : 'Mot de passe'}
              type="password"
              placeholder={isEdit ? 'Laisser vide pour ne pas modifier' : 'Minimum 6 caractères'}
              value={form.password}
              onChange={(e) => handleChange('password', e.target.value)}
              required={!isEdit}
            />
            {isEdit && (
              <p className="mt-1 text-sm text-slate-500">
                Laisser vide pour conserver le mot de passe actuel.
              </p>
            )}
          </div>

          {/* Statut (édition uniquement) */}
          {isEdit && (
            <div className="border-t pt-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Statut</h2>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => handleChange('is_active', e.target.checked)}
                  className="w-4 h-4 text-carex-600 border-slate-300 rounded focus:ring-carex-500"
                />
                <span className="text-slate-700">Compte actif</span>
              </label>
            </div>
          )}
        </Card>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 mt-6">
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate('/admin/utilisateurs')}
          >
            Annuler
          </Button>
          <Button type="submit" variant="primary" disabled={!isValid || loading}>
            <Save className="w-4 h-4" />
            {loading ? 'Enregistrement...' : isEdit ? 'Enregistrer' : "Créer l'utilisateur"}
          </Button>
        </div>
      </form>
    </div>
  );
}
